import * as React from "react";
import { cn } from "../utils/cn";
import { Avatar, AvatarGradientFallback } from "./avatar";
import { Badge } from "./badge";
import { Mono, Text } from "../primitives/typography";

export interface ChatItemProps extends React.HTMLAttributes<HTMLDivElement> {
    name: string;
    organizationName?: string;
    messagePreview: string;
    timestamp?: string;
    unreadCount?: number;
    tag?: string;
    avatarFallback: string;
    avatarFrom?: string;
    avatarTo?: string;
    variant?: "default" | "selected" | "accented" | "urgent";
}

const ChatItem = React.forwardRef<HTMLDivElement, ChatItemProps>(
    (
        {
            className,
            name,
            organizationName,
            messagePreview: preview,
            timestamp,
            unreadCount,
            tag,
            avatarFallback,
            avatarFrom,
            avatarTo,
            variant = "default",
            ...props
        },
        ref,
    ) => {
        const isSelected = variant === "selected";
        const isAccented = variant === "accented";
        const isUrgent = variant === "urgent";

        return (
            <div
                ref={ref}
                className={cn(
                    "bg-muted hover:bg-muted/80 border-border relative flex cursor-pointer items-center gap-3 border px-4 py-3 transition-colors",
                    isSelected && "border-secondary border-2",
                    isAccented && "bg-muted border-l-secondary rounded-l-none border-l-4",
                    className,
                )}
                {...props}
            >
                <Avatar className="h-12 w-12 shrink-0">
                    <AvatarGradientFallback from={avatarFrom} to={avatarTo}>
                        {avatarFallback}
                    </AvatarGradientFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <Text className={cn("font-display truncate font-semibold", isUrgent && "text-destructive")}>
                            {name}
                        </Text>
                        {timestamp && (
                            <Mono size="xs" muted className="shrink-0">
                                {timestamp}
                            </Mono>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {organizationName && <span className="text-muted-foreground text-sm">{organizationName}</span>}
                        {tag && (
                            <span className="bg-secondary/10 text-secondary rounded px-1 py-0.5 font-mono text-[10px] font-medium tracking-wider uppercase">
                                {tag}
                            </span>
                        )}
                    </div>

                    <div className="mt-0.5 flex items-center justify-between gap-2">
                        <Text className="text-muted-foreground truncate text-sm">
                            {isUrgent && <span className="mr-1">🚨</span>}
                            {preview}
                        </Text>
                        {unreadCount !== undefined && unreadCount > 0 && (
                            <Badge className="bg-secondary text-secondary-foreground h-5 min-w-5 shrink-0 justify-center rounded-full px-1.5 text-xs">
                                {unreadCount}
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
ChatItem.displayName = "ChatItem";

export { ChatItem };
